import { useState, useImperativeHandle, forwardRef } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { CreditCard, Lock, AlertCircle, RefreshCw } from "lucide-react";

interface PaymentFormProps {
  onPaymentMethodCreated: (paymentMethod: {
    id: string;
    [key: string]: unknown;
  }) => void;
  loading: boolean;
  onClearErrors?: () => void;
  paymentError?: {
    code: string;
    declineCode: string;
    message: string;
  } | null;
  isCustomerFormValid?: boolean;
  hasPricingError?: boolean;
}

interface PaymentFormRef {
  clearElements: () => void;
}

export const PaymentForm = forwardRef<PaymentFormRef, PaymentFormProps>(
  (
    {
      onPaymentMethodCreated,
      loading,
      onClearErrors,
      paymentError,
      isCustomerFormValid = true,
      hasPricingError = false,
    },
    ref,
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleCardChange = () => {
      if (onClearErrors) {
        onClearErrors();
      }
    };

    const clearStripeElements = () => {
      if (elements) {
        const cardElement = elements.getElement(CardElement);
        if (cardElement) {
          cardElement.clear();
        }
      }
    };

    useImperativeHandle(ref, () => ({
      clearElements: clearStripeElements,
    }));

    const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault();

      // Validate customer form first
      if (!isCustomerFormValid) {
        setError("Please complete all required customer information fields");
        return;
      }

      if (!stripe || !elements) return;
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      setIsProcessing(true);
      setError(null);

      try {
        const { paymentMethod, error: stripeError } =
          await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name:
                (document.getElementById("customer_name") as HTMLInputElement)
                  ?.value || "",
              email:
                (document.getElementById("customer_email") as HTMLInputElement)
                  ?.value || "",
              phone:
                (document.getElementById("customer_phone") as HTMLInputElement)
                  ?.value || "",
            },
          });

        if (stripeError) {
          setError(stripeError.message || "Failed to create payment method");
          return;
        }
        if (paymentMethod) {
          onPaymentMethodCreated(
            paymentMethod as unknown as { id: string; [key: string]: unknown },
          );
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsProcessing(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Card Information *
          </label>
          <div className="relative">
            <div className="pl-2 pr-2 py-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-colors">
              <CardElement
                onChange={handleCardChange}
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#374151",
                      fontFamily: "system-ui, sans-serif",
                      "::placeholder": {
                        color: "#9CA3AF",
                      },
                    },
                    invalid: {
                      color: "#EF4444",
                    },
                  },
                }}
              />
            </div>
          </div>

          {error && (
            <div className="mt-3 flex items-center">
              <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {paymentError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-600 font-medium">
                    {paymentError.message}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (onClearErrors) onClearErrors();
                      clearStripeElements();
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Try again with a different card
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-start">
            <Lock className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-green-800 font-medium">
                Secure Payment
              </p>
              <p className="text-xs text-green-600 mt-1">
                Your payment information is encrypted and processed securely by
                Stripe. We never store your card details.
              </p>
            </div>
          </div>
        </div>

        {/* Form Validation Notice */}
        {!isCustomerFormValid && (
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-800 font-medium">
                  Complete Required Information
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Please fill in all customer information fields above before
                  proceeding with payment.
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={
            !stripe ||
            isProcessing ||
            loading ||
            !isCustomerFormValid ||
            hasPricingError
          }
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-4 px-6 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transform hover:scale-[1.02]"
        >
          {isProcessing || loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
              Processing Payment...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <Lock className="h-5 w-5 mr-2" />
              Complete Reservation
            </div>
          )}
        </button>
      </form>
    );
  },
);

PaymentForm.displayName = "PaymentForm";
